import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  appwriteSessionGateway,
  type SessionGateway,
} from '../../services/appwrite/session-gateway';
import type { SessionProfile } from '../../types/session';

export type SessionState =
  | { status: 'checking' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; profile: SessionProfile }
  | { status: 'unavailable'; message: string };

interface SessionContextValue {
  state: SessionState;
  refresh(): Promise<void>;
  signIn(): Promise<void>;
  signInWithPassword(email: string, password: string): Promise<void>;
  registerWithPassword(email: string, password: string, name: string): Promise<void>;
  requestPasswordReset(email: string): Promise<void>;
  confirmPasswordReset(userId: string, secret: string, password: string): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps extends PropsWithChildren {
  gateway?: SessionGateway;
}

function readableError(error: unknown): string {
  if (error instanceof Error && /failed to fetch|network|load failed/i.test(error.message)) {
    return 'No pudimos contactar con el servicio de acceso. Revisa tu conexión e inténtalo de nuevo.';
  }
  return 'No pudimos comprobar tu acceso en este momento. Inténtalo de nuevo.';
}

export function SessionProvider({ children, gateway = appwriteSessionGateway }: SessionProviderProps) {
  const [state, setState] = useState<SessionState>({ status: 'checking' });

  const refresh = useCallback(async () => {
    setState({ status: 'checking' });
    try {
      const profile = await gateway.load();
      setState(profile ? { status: 'authenticated', profile } : { status: 'anonymous' });
    } catch (error) {
      console.error('[Club Accesible] No se pudo recuperar la sesión.', error);
      setState({ status: 'unavailable', message: readableError(error) });
    }
  }, [gateway]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async () => gateway.signInWithGoogle(), [gateway]);
  const signInWithPassword = useCallback(
    async (email: string, password: string) => {
      await gateway.signInWithPassword(email, password);
      await refresh();
    },
    [gateway, refresh],
  );
  const registerWithPassword = useCallback(
    async (email: string, password: string, name: string) => {
      await gateway.registerWithPassword(email, password, name);
      await refresh();
    },
    [gateway, refresh],
  );
  const requestPasswordReset = useCallback(
    async (email: string) => gateway.requestPasswordReset(email),
    [gateway],
  );
  const confirmPasswordReset = useCallback(
    async (userId: string, secret: string, password: string) => {
      await gateway.confirmPasswordReset(userId, secret, password);
      await refresh();
    },
    [gateway, refresh],
  );
  const signOut = useCallback(async () => {
    await gateway.signOut();
    setState({ status: 'anonymous' });
  }, [gateway]);

  const value = useMemo(
    () => ({ state, refresh, signIn, signInWithPassword, registerWithPassword, requestPasswordReset, confirmPasswordReset, signOut }),
    [state, refresh, signIn, signInWithPassword, registerWithPassword, requestPasswordReset, confirmPasswordReset, signOut],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession debe utilizarse dentro de SessionProvider.');
  return context;
}
